import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { BILLING_INTERVALS } from '../../../constants/billingCatalog';
import { getPlanName } from '../../../lib/utils/billingLabels';

const PLAN_IDS = ['essential', 'pro', 'essential-and-voice', 'pro-and-voice'];

const PLAN_META = {
  essential: { icons: ['Eye'], highlighted: false },
  pro: { icons: ['Database'], highlighted: false },
  'essential-and-voice': { icons: ['Phone'], highlighted: true },
  'pro-and-voice': { icons: ['Phone', 'Database'], highlighted: true },
};

const GROUPS = [
  {
    titleKey: 'billing.compare.groups.outcomes',
    rows: [
      { labelKey: 'billing.compare.rows.signals', values: mark('check', 'check', 'check', 'check') },
      { labelKey: 'billing.compare.rows.exchange', values: mark('csv', 'ats', 'csv', 'ats') },
      { labelKey: 'billing.compare.rows.preferences', values: mark('minus', 'minus', 'check', 'check') },
      { labelKey: 'billing.compare.rows.interview', values: mark('minus', 'minus', 'check', 'check') },
      { labelKey: 'billing.compare.rows.summary', values: mark('minus', 'minus', 'check', 'check') },
      { labelKey: 'billing.compare.rows.results', values: mark('minus', 'minus', 'minus', 'check') },
    ],
  },
  {
    titleKey: 'billing.compare.groups.capacity',
    rows: [
      { labelKey: 'billing.compare.rows.candidates', values: mark(1000, 5000, 1000, 5000) },
      { labelKey: 'billing.compare.rows.minutes', values: mark('minus', 'minus', 'minutes', 'minutes') },
    ],
  },
];

function mark(essential, pro, essentialVoice, proVoice) {
  return {
    essential,
    pro,
    'essential-and-voice': essentialVoice,
    'pro-and-voice': proVoice,
  };
}

function formatEur(cents, { locale, digits }) {
  if (cents == null || Number.isNaN(cents)) return null;
  return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : 'en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(cents / 100);
}

const PLAN_HIGHLIGHT = 'bg-[oklch(96.2%_0.021_258)]';

function columnBackground(highlighted) {
  return highlighted ? PLAN_HIGHLIGHT : 'bg-card';
}

function FeatureValue({ value, locale }) {
  const { t } = useTranslation();

  if (value === 'check' || value === 'minus') {
    const included = value === 'check';
    return (
      <span className="inline-flex items-center justify-center">
        <span className="sr-only">{included ? t('billing.compare.included') : t('billing.compare.notIncluded')}</span>
        <Icon
          name={included ? 'Check' : 'Minus'}
          size={included ? 20 : 16}
          color={included ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
        />
      </span>
    );
  }

  if (value === 'minutes') {
    return (
      <span className="block text-sm leading-relaxed">
        <span className="block">{t('billing.compare.minutesPrimary')}</span>
        <span className="block text-muted-foreground">{t('billing.compare.minutesSecondary')}</span>
      </span>
    );
  }

  if (value === 'csv' || value === 'ats') {
    return <span className="text-sm">{t(`billing.compare.${value}`)}</span>;
  }

  if (typeof value === 'number') {
    return (
      <span className="text-sm">
        {new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : 'en-US').format(value)}
      </span>
    );
  }

  return null;
}

function PlanOffer({
  product,
  interval,
  onIntervalChange,
  isCurrent,
  currentInterval,
  canSwitch,
  selectingKey,
  onSelectPlan,
  onChangePlan,
}) {
  const { t, i18n } = useTranslation();
  const meta = PLAN_META[product.id];
  const monthlyPrice = product.prices?.[BILLING_INTERVALS.MONTH];
  const yearlyPrice = product.prices?.[BILLING_INTERVALS.YEAR];
  const selectedPrice = interval === BILLING_INTERVALS.YEAR ? yearlyPrice : monthlyPrice;
  const hasSelectedPrice = Boolean(selectedPrice?.amount);
  const locale = i18n.language?.startsWith('nl') ? 'nl' : 'en';
  const selectKey = `${product.id}-${interval}`;
  const isSelecting = selectingKey === selectKey;
  const effectiveCurrentInterval = currentInterval || BILLING_INTERVALS.MONTH;
  const isCurrentPrice = isCurrent && interval === effectiveCurrentInterval;
  const isSwitch = canSwitch && !isCurrentPrice;

  const dailyCents = hasSelectedPrice
    ? (interval === BILLING_INTERVALS.YEAR
      ? selectedPrice.amount / 12 / 30
      : selectedPrice.amount / 30)
    : null;
  const equivalentMonthlyCents = interval === BILLING_INTERVALS.YEAR && hasSelectedPrice
    ? selectedPrice.amount / 12
    : null;

  const handleSelect = () => {
    const payload = { product, interval, price: selectedPrice };
    if (isSwitch) onChangePlan?.(payload);
    else onSelectPlan?.(payload);
  };

  return (
    <div className="flex h-full flex-col items-center text-center">
      <div className="mb-3 flex justify-center gap-2">
        {meta.icons.map((name) => (
          <span
            key={name}
            className="grid h-10 w-10 place-items-center rounded-full bg-card text-primary shadow-sm"
          >
            <Icon name={name} size={20} color="currentColor" />
          </span>
        ))}
      </div>
      <h3 className="text-lg font-semibold leading-snug lg:min-h-14">
        {t(`billing.compare.columns.${product.id}.outcome`)}
      </h3>
      <p className="mt-2 text-sm font-medium leading-snug lg:min-h-10">
        {getPlanName(product.id)}
      </p>
      <p className="mt-2 text-[13px] font-normal leading-relaxed text-muted-foreground lg:min-h-20">
        {t(`billing.compare.columns.${product.id}.summary`)}
      </p>

      <div className="mb-4 mt-4 flex w-full rounded-lg border border-border bg-card p-1">
        <button
          type="button"
          onClick={() => onIntervalChange(BILLING_INTERVALS.MONTH)}
          className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
            interval === BILLING_INTERVALS.MONTH
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          aria-pressed={interval === BILLING_INTERVALS.MONTH}
        >
          {t('billing.monthly')}
        </button>
        <button
          type="button"
          onClick={() => onIntervalChange(BILLING_INTERVALS.YEAR)}
          className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
            interval === BILLING_INTERVALS.YEAR
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          aria-pressed={interval === BILLING_INTERVALS.YEAR}
        >
          {t('billing.yearly')}
        </button>
      </div>

      {hasSelectedPrice ? (
        <div className="lg:min-h-24">
          <p className="text-3xl font-semibold tabular-nums">
            {formatEur(dailyCents, { locale, digits: 2 })}
            <span className="text-sm font-medium">{` ${t('billing.compare.perDay')}`}</span>
          </p>
          <p className="mt-2 text-sm font-normal leading-relaxed">
            {interval === BILLING_INTERVALS.YEAR
              ? t('billing.compare.perYearExcl', {
                price: formatEur(selectedPrice.amount, { locale, digits: 0 }),
              })
              : t('billing.compare.perMonthExcl', {
                price: formatEur(selectedPrice.amount, { locale, digits: 0 }),
              })}
          </p>
          {equivalentMonthlyCents != null && (
            <p className="text-sm font-normal leading-relaxed text-muted-foreground">
              {t('billing.compare.equivalentMonth', {
                price: formatEur(equivalentMonthlyCents, { locale, digits: 0 }),
              })}
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground lg:min-h-24">{t('billing.priceUnavailable')}</p>
      )}

      <Button
        variant={isCurrentPrice ? 'outline' : 'default'}
        size="sm"
        fullWidth
        className="mt-3 rounded-full"
        disabled={isCurrentPrice || !hasSelectedPrice || !selectedPrice?.priceId || isSelecting}
        loading={isSelecting}
        onClick={handleSelect}
      >
        {isCurrentPrice
          ? t('billing.currentPlan')
          : isSwitch
            ? (isCurrent
              ? (interval === BILLING_INTERVALS.YEAR
                ? t('billing.switchToYearly')
                : t('billing.switchToMonthly'))
              : t('billing.switchToPlan'))
            : t('billing.selectPlan')}
      </Button>
      <p className="mt-2 text-xs font-normal text-muted-foreground">
        {t('billing.compare.trialNote')}
      </p>
    </div>
  );
}

const PlanComparisonTable = ({
  products = [],
  loading = false,
  onSelectPlan,
  onChangePlan,
  selectingKey,
  currentPlanId = null,
  currentInterval = null,
  canSwitch = false,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('nl') ? 'nl' : 'en';
  const [intervals, setIntervals] = useState(() => (
    Object.fromEntries(PLAN_IDS.map((id) => [id, BILLING_INTERVALS.MONTH]))
  ));

  const plans = PLAN_IDS.map((id) => (
    products.find((product) => product.id === id) || { id, prices: {} }
  ));
  const anyYearly = PLAN_IDS.some((id) => intervals[id] === BILLING_INTERVALS.YEAR);
  const anyMonthly = PLAN_IDS.some((id) => intervals[id] !== BILLING_INTERVALS.YEAR);

  const setIntervalFor = (id, interval) => {
    setIntervals((current) => ({ ...current, [id]: interval }));
  };

  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-4">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="h-96 animate-pulse rounded-lg border border-border bg-muted/20" />
        ))}
      </div>
    );
  }

  const offerProps = (product) => ({
    product,
    interval: intervals[product.id] || BILLING_INTERVALS.MONTH,
    onIntervalChange: (interval) => setIntervalFor(product.id, interval),
    isCurrent: currentPlanId != null && product.id === currentPlanId,
    currentInterval,
    canSwitch,
    selectingKey,
    onSelectPlan,
    onChangePlan,
  });

  return (
    <div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[960px] table-fixed border-collapse text-sm">
          <caption className="sr-only">{t('billing.availablePlans')}</caption>
          <colgroup>
            <col className="w-[26%]" />
            <col className="w-[18.5%]" />
            <col className="w-[18.5%]" />
            <col className="w-[18.5%]" />
            <col className="w-[18.5%]" />
          </colgroup>
          <thead>
            <tr>
              <td aria-hidden="true" />
              {plans.map((product) => (
                <th
                  key={product.id}
                  scope="col"
                  className={`border border-border p-4 align-top ${columnBackground(PLAN_META[product.id].highlighted)}`}
                >
                  <PlanOffer {...offerProps(product)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GROUPS.map((group) => (
              <React.Fragment key={group.titleKey}>
                <tr>
                  <th
                    colSpan={5}
                    className={`border border-border px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${PLAN_HIGHLIGHT}`}
                  >
                    {t(group.titleKey)}
                  </th>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.labelKey}>
                    <th
                      scope="row"
                      className="border border-border bg-card px-4 py-3 text-left font-normal leading-relaxed"
                    >
                      {t(row.labelKey)}
                    </th>
                    {plans.map((product) => (
                      <td
                        key={`${product.id}-${row.labelKey}`}
                        className={`border border-border px-3 py-3 text-center ${columnBackground(PLAN_META[product.id].highlighted)}`}
                      >
                        <FeatureValue value={row.values[product.id]} locale={locale} />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 lg:hidden">
        {plans.map((product) => (
          <article
            key={product.id}
            className={`border border-border p-4 ${columnBackground(PLAN_META[product.id].highlighted)}`}
          >
            <PlanOffer {...offerProps(product)} />
            <div className="mt-5 space-y-4 border-t border-border pt-4 text-left">
              {GROUPS.map((group) => (
                <div key={group.titleKey}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(group.titleKey)}
                  </p>
                  <ul className="mt-2 space-y-2">
                    {group.rows.map((row) => (
                      <li key={row.labelKey} className="flex items-center justify-between gap-3">
                        <span className="text-sm">{t(row.labelKey)}</span>
                        <span className="shrink-0 text-right">
                          <FeatureValue value={row.values[product.id]} locale={locale} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        {[
          t('billing.compare.footnoteAts'),
          anyMonthly ? t('billing.compare.footnoteMonth') : null,
          anyYearly ? t('billing.compare.footnoteYear') : null,
        ].filter(Boolean).join(' | ')}
      </p>
    </div>
  );
};

export default PlanComparisonTable;
